import { Badge } from "@astryxdesign/core/Badge";
import { Button } from "@astryxdesign/core/Button";
import { Card } from "@astryxdesign/core/Card";
import { CheckboxInput } from "@astryxdesign/core/CheckboxInput";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { Grid } from "@astryxdesign/core/Grid";
import { HStack, VStack } from "@astryxdesign/core/Stack";
import { Text } from "@astryxdesign/core/Text";
import { TextArea } from "@astryxdesign/core/TextArea";
import { ToolShell } from "@karrtopelka/tools-kit/ToolShell";
import { useMemo, useState } from "react";
import { collapse, diffLines, MAX_LINES, type DiffLine } from "./lib/diff.js";

const SIGN: Record<DiffLine["op"], string> = { same: " ", add: "+", remove: "−" };

/**
 * One diff line.
 *
 * The +/− sign and an accent-coloured Badge carry the change, rather than a row tint:
 * this project has no StyleX build step, so `xstyle` would silently do nothing, and
 * inline `style` is off-limits. Colour alone would fail contrast checks anyway — the
 * sign is the accessible signal and the badge reinforces it.
 */
function Row({ line }: { line: DiffLine }) {
  return (
    <HStack gap={2} align="start" paddingInline={2} paddingBlock={0.5}>
      <Text type="code" size="sm" color="disabled" hasTabularNumbers>
        {String(line.leftNo ?? "·").padStart(4, " ")}
      </Text>
      <Text type="code" size="sm" color="disabled" hasTabularNumbers>
        {String(line.rightNo ?? "·").padStart(4, " ")}
      </Text>
      <Text
        type="code"
        size="sm"
        color={line.op === "same" ? "secondary" : line.op === "add" ? "accent" : "primary"}
        weight={line.op === "same" ? "normal" : "medium"}
        hasStrikethrough={line.op === "remove"}
        textWrap="pretty"
      >
        {SIGN[line.op]} {line.text || " "}
      </Text>
    </HStack>
  );
}

export function App() {
  const [left, setLeft] = useState("");
  const [right, setRight] = useState("");
  const [hideUnchanged, setHideUnchanged] = useState(true);

  const result = useMemo(() => {
    if (left === "" && right === "") return null;
    return diffLines(left, right);
  }, [left, right]);

  const rows = useMemo(() => {
    if (!result) return null;
    if (!hideUnchanged) return result.lines;
    return collapse(result.lines) ?? result.lines;
  }, [result, hideUnchanged]);

  const identical = result !== null && result.added === 0 && result.removed === 0;

  return (
    <ToolShell active="diff" tagline="Compare two blocks of text">
      <Grid columns={2} gap={2}>
        <TextArea
          label="Original"
          placeholder="Paste the first version"
          value={left}
          onChange={setLeft}
          rows={10}
          hasSpellCheck={false}
        />
        <TextArea
          label="Changed"
          placeholder="Paste the second version"
          value={right}
          onChange={setRight}
          rows={10}
          hasSpellCheck={false}
        />
      </Grid>

      <HStack gap={2} align="center" justify="between" wrap="wrap">
        <CheckboxInput
          label="Collapse unchanged lines"
          value={hideUnchanged}
          onChange={setHideUnchanged}
        />
        <Button
          variant="ghost"
          size="sm"
          label="Clear both"
          isDisabled={left === "" && right === ""}
          onClick={() => {
            setLeft("");
            setRight("");
          }}
        />
      </HStack>

      {result === null ? (
        <EmptyState
          title="Paste two versions"
          description="Anything text — config files, logs, prose. The comparison runs as you type."
        />
      ) : identical ? (
        <EmptyState
          title="No differences"
          description="Both sides are identical, line for line."
        />
      ) : (
        <VStack gap={1}>
          <HStack gap={1} align="center">
            <Text type="label" color="secondary">
              Changes
            </Text>
            <Badge label={`+${result.added}`} variant="green" />
            <Badge label={`−${result.removed}`} variant="red" />
            {result.truncated ? (
              <Text type="supporting" size="xsm" color="secondary">
                truncated at {MAX_LINES} lines per side
              </Text>
            ) : null}
          </HStack>

          <Card padding={0}>
            <VStack gap={0} paddingBlock={1}>
              {rows?.map((row, i) =>
                row === "gap" ? (
                  <HStack key={`gap-${i}`} paddingInline={2} paddingBlock={0.5}>
                    <Text type="code" size="sm" color="disabled">
                      ⋯ unchanged lines hidden
                    </Text>
                  </HStack>
                ) : (
                  <Row key={`${row.leftNo}-${row.rightNo}-${i}`} line={row} />
                ),
              )}
            </VStack>
          </Card>
        </VStack>
      )}

      <Text type="supporting" size="xsm" color="secondary">
        Everything runs in your browser. Nothing you paste is uploaded, stored or logged.
      </Text>
    </ToolShell>
  );
}
